/* eslint-disable no-mixed-spaces-and-tabs */
import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import {
  Button, Grid, TextField, Checkbox, TableContainer, Table,
  TableHead, TableBody, TableRow, TableCell, Typography, TableSortLabel,
  SvgIcon, InputAdornment, Switch, Autocomplete, Divider, Snackbar, Alert,
  FormControlLabel, useMediaQuery, Box, Tooltip, useTheme,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { DateField } from '@mui/x-date-pickers';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SaveIcon from '@mui/icons-material/Save';
import { MagicWand01, Plus, SearchSm } from './../../../assets';
import { OrderKind } from '../../../enums/OrderKind';
import { isAutoShipOrder, isOrderTypeDSD, OrderTypeTranslationMap } from '../../../enums/OrderType';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../../../components/shared/useUser';

import { client, SimpleCategory, DeliverySchedule, OrderDetails, OrderItemDetails, CutOffTimeLeft, Item, OrderlySystem } from '../../../app/services/api/orderManagementClient';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import dayjs, { Dayjs } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import OrderStatusChip from '../../../components/shared/orders/OrderStatusChip';
import CopyOrderModal from '../../../components/shared/orders/CopyOrderModal';
import { ConfirmationDialog } from '../../../components/shared/dialogs/ConfirmationDialog';
import { OrderStatus } from '../../../enums/OrderStatus';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import usePodPredictionReadonlyConfiguration from '../../../hooks/configurations/usePodPredictionReadonlyConfiguration';
import { applyRoundAndFormat, getDateFormatByFranchise, getInventorySku } from '../../../utils';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import BackButton from '../../../components/shared/actions/BackButton';
import { useTranslation } from 'react-i18next';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import { ProductDetailsCard } from '../../../components/shared/orders/ProductDetailsCard';
import { starbucksFranchises } from '../../../enums/FranchiseNames';
import { isOrderCategory, OrderCategoriesTranslationMap } from '../../../enums/OrderCategories';
import { PredictedDemandModel, podClient, ItemConversion } from './../../../app/services/api/podClient';
import LeavePageModal from '../../../components/shared/dialogs/LeavePageModal';
import './OrderDetails.css';
import { QuantityWarningDialog } from './../../../components/shared/orders/QuantityWarningDialog';
import OrderAuditModal from './../../../components/shared/orders/OrderAuditModal';
import { isPodEnabledEnvironment } from './../../../app/services/environment';


interface DialogState {
  open: boolean;
  title: string;
  message: string;
  confirmAction: () => void;
}

const OrderDetailsPage = () => {
  dayjs.extend(duration);
  const navigate = useNavigate();
  const { selectedStore, hasPermissionTo, hasPremiumModuleAccess } = useContext(UserContext);
  const canEditPredictions = hasPermissionTo(['AIOrderingAdmin', 'AIOrderingEditPredictions']);
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const { isPodPredictionReadonlyEnabled, getPodPredictionReadonlyConfiguration } = usePodPredictionReadonlyConfiguration();
  const { t } = useTranslation('orderDetails');
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.sm}px)`);

  const [loading, setLoading] = useState(true);
  const [orderType, setOrderType] = useState<string>('');
  const [orderTypeId, setOrderTypeId] = useState<number>(0);
  const [deliveryDate, setDeliveryDate] = useState<Dayjs | null>(null);
  const [cutOffDate, setCutOffDate] = useState<Dayjs | null>(null);
  const [orderKind, setOrderKind] = useState(OrderKind.Emergency);

  const [productSearch, setProductSearch] = useState('');
  const [category, setCategory] = useState<number>(-1);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('Name');

  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>();
  const [filteredItems, setFilteredItems] = useState<OrderItemDetails[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([]);
  const [categories, setCategories] = useState<SimpleCategory[]>([]);
  const [deliverySchedules, setDeliverySchedules] = useState<DeliverySchedule[]>([]);

  const [itemQuantities, setItemQuantities] = useState<{ [key: number]: number }>({});
  const [clearedQuantityItemIds, setClearedQuantityItemIds] = useState<Set<number>>(new Set());
  const [isCopyOrderModalOpen, setIsCopyOrderModalOpen] = useState(false);
  const [isOrderAuditModalOpen, setIsOrderAuditModalOpen] = useState(false);
  const [isPodEnabled, setIsPodEnabled] = useState(false);

  const [items, setItems] = useState<Item[]>([]);
  const [showPredictedQuantity, setShowPredictedQuantity] = useState<boolean>(true);
  const [predictedDemands, setPredictedDemands] = useState<PredictedDemandModel[]>([]);
  const [modifiedItems, setModifiedItems] = useState<Set<number>>(new Set());
  const [lastFocusedItemId, setLastFocusedItemId] = useState<number | null>(null);
  const [showAutosaveMessage, setShowAutosaveMessage] = useState<boolean>(false);
  const [inactivityTimer, setInactivityTimer] = useState<NodeJS.Timeout | null>(null);

  const INACTIVITY_TIMEOUT = 60000; // 1 min on milliseconds

  const [navigateToIndex, setNavigateToIndex] = useState<boolean>(false);
  const [isExitAndSaveClicked, setIsExitAndSaveClicked] = useState<boolean>(false);

  const [showQuantityWarningDialog, setShowQuantityWarningDialog] = useState<boolean>(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [minimumOrderValue, setMinimumOrderValue] = useState<number>(0);

  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    title: '',
    message: '',
    confirmAction: () => { },
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const isParEnabled = orderDetails?.isParEnabled;

  const { id } = useParams();

  const isStarBucksRetail = starbucksFranchises.includes(selectedStore?.franchiseName ?? '');
  const showMessageWhenNoCost = isStarBucksRetail && orderDetails?.status != 'Order Confirmed';

  const blocker = useBlocker(() => modifiedItems.size > 0 && !isExitAndSaveClicked);

  useEffect(() => {
    getCurrencyCodeConfiguration();
    getPodPredictionReadonlyConfiguration();
  }, []);

  useEffect(() => {
    loadData();

    if (isPodEnabledEnvironment()) {
      loadPodStoreConfiguration();
    }
  }, [selectedStore, id]);

  useEffect(() => {
    if (orderDetails) {
      const categoriesById: Record<number, SimpleCategory> = {};
      orderDetails.items.forEach((item) => {
        if (!categoriesById[item.categoryId]) {
          categoriesById[item.categoryId] = { id: item.categoryId, name: item.category };
        }
      });
      setCategories(Object.values(categoriesById));
    }
  }, [orderDetails]);

  useEffect(() => {
    const canLoadPredictions = isPodPredictionReadonlyEnabled || hasPermissionTo(['AIOrderingAdmin', 'AIOrderingEditPredictions']);
    if (isPodEnabled && hasPremiumModuleAccess('Predictive Ordering Settings') && deliveryDate && items.length > 0 && canLoadPredictions) {
      loadPredictedDemands();
    }

  }, [isPodEnabled, isPodPredictionReadonlyEnabled, deliveryDate, items]);

  useEffect(() => {
    if (predictedDemands.length > 0 && orderDetails && orderDetails.status === OrderStatus.Editable) {
      let newItems = [...orderDetails.items];

      if (showPredictedQuantity) {
        const itemsToAdd = getAditionalItemsWithPredictions();

        newItems.push(...itemsToAdd);
      }
      else {
        newItems = newItems.filter(x => x.id > 0);
      }

      setOrderDetails((prev) => {
        return {
          ...prev!,
          items: newItems,
        };
      });

      setFilteredItems(newItems);
    }
  }, [predictedDemands, showPredictedQuantity]);

  useEffect(() => {
    if (modifiedItems.size > 0) {
      handleAutosaveMessage(false);
      startInactivityTimer();
    }
  }, [itemQuantities, modifiedItems, orderDetails?.items.length]);


  useEffect(() => {
    const beforeUnload = (event: { preventDefault: () => void; returnValue: string; }) => {
      if (modifiedItems.size > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [modifiedItems]);

  useEffect(() => {
    if (navigateToIndex) {
      navigate('/store/order');
      if (blocker?.state === 'blocked') {
        blocker.proceed();
      }
      setNavigateToIndex(false);
    }
  }, [navigateToIndex, blocker]);

  const handleLeavePageModalCancel = () => {
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const handleLeavePageModalClose = () => {
    if (blocker?.reset) {
      blocker.reset();
    }
  };

  const handleLeavePageModalConfirm = () => {
    handleSaveChanges();
    if (blocker?.proceed) {
      blocker.proceed();
    }
  };

  const markItemAsModified = (itemId: number) => {
    setModifiedItems((prev) => {
      const nextModifiedItems = new Set(prev);
      nextModifiedItems.add(itemId);
      return nextModifiedItems;
    });
  };

  const getCurrentQuantity = (item: OrderItemDetails) => {
    if (clearedQuantityItemIds.has(item.itemId)) {
      return getDisplayedParQuantity(item) ?? item.quantity;
    }

    return itemQuantities[item.itemId] ?? item.quantity;
  };

  const getDisplayedParQuantity = (item: OrderItemDetails) => {
    return item.calculatedParQuantity ?? item.parQuantityApplied;
  };

  const isPersistedParOwnedItem = (item: OrderItemDetails) => {
    const displayedParQuantity = getDisplayedParQuantity(item);

    return orderDetails?.isParEnabled === true
      && item.isQuantityOverridden !== true
      && displayedParQuantity != null
      && item.quantity === displayedParQuantity;
  };





  const clearModifiedItem = (itemId: number) => {
    setModifiedItems((prev) => {
      if (!prev.has(itemId)) {
        return prev;
      }

      const nextModifiedItems = new Set(prev);
      nextModifiedItems.delete(itemId);
      return nextModifiedItems;
    });
  };



  const setCurrentQuantity = (itemId: number, quantity: number) => {
    markItemAsModified(itemId);
    setItemQuantities((prev) => ({
      ...prev,
      [itemId]: quantity,
    }));
  };





  const buildUpdatedItemsFromOrderItems = (
    orderItems: OrderItemDetails[],
    quantities: Record<number, number>,
  ) => {
    return orderItems
      .filter((item) => item.id > 0 || (quantities[item.itemId] ?? item.quantity) > 0)
      .map((item) => ({
        id: item.itemId,
        quantity: clearedQuantityItemIds.has(item.itemId)
          ? null
          : (quantities[item.itemId] ?? item.quantity),
        predictedQuantity: predictedDemands.find((prediction) => prediction.itemNumber === getInventorySku({ sku: item.sku }))?.predictedDemandItems[0].quantity,
      }));
  };


  const buildUpdatedItems = (quantities: Record<number, number> = itemQuantities) => {
    if (!orderDetails) {
      return [];
    }

    return buildUpdatedItemsFromOrderItems(orderDetails.items, quantities);
  };







  const startInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    const timer = setTimeout(async () => {
      if (modifiedItems.size > 0) {
        modifiedItems.forEach(itemId => {
          autosaveItem(itemId);
        });
      }
    }, INACTIVITY_TIMEOUT);

    setInactivityTimer(timer);
  };

  const handleAutosaveMessage = (changesSaved: boolean) => {
    setShowAutosaveMessage(changesSaved);
  };

  const handleItemFocus = (itemId: number) => {
    if (lastFocusedItemId !== null && lastFocusedItemId !== itemId && modifiedItems.has(lastFocusedItemId)) {

      autosaveItem(lastFocusedItemId);
    }
    setLastFocusedItemId(itemId);
  };

  const autosaveItem = async (itemId: number) => {
    if (!orderDetails) return;

    const updatedItems = buildUpdatedItems();

    const updatedOrder = {
      id: orderDetails.id,
      items: updatedItems,
      orderlySystem: OrderlySystem.OFS,
    };

    try {
      const result = await client.updateOrder(selectedStore?.storeNumber, updatedOrder);
      if (result.success) {
        handleAutosaveMessage(true);
        setModifiedItems(prev => {
          const newModifiedItems = new Set(prev);
          newModifiedItems.delete(itemId);
          return newModifiedItems;
        });
      }
    } catch (error) {
      handleSnackbarOpen(t('updateError') + ' ' + error, 'error');
    }
  };

  const loadData = async () => {
    setLoading(true);
    await fetchOrderDetails();
    setLoading(false);
  };

  const updateOrderEditStatus = (order: OrderDetails) => {
    if (isAutoShipOrder(order.orderTypeId)) {
      return OrderStatus.Autoshipment;
    }
    return order.status;
  };

  const fetchDeliverySchedules = async (selectedOrderType: number | undefined, supplierNumber: string) => {
    try {
      const deliverySchedulesResponse = await client.getDeliverySchedules(selectedStore?.storeNumber, selectedOrderType, supplierNumber);
      setDeliverySchedules(deliverySchedulesResponse.data || []);
    } catch (error) {
      handleSnackbarOpen(t('loadingErrorMessage') + ' ' + error, 'error');
    }
  };
  const fetchOrderDetails = async (orderId?: number) => {
    setMinimumOrderValue(0);
    try {
      const orderIdToFetch = orderId ?? parseInt(id ?? '');
      const result = await client.getOrder(selectedStore?.storeNumber, orderIdToFetch);
      result.data.status = updateOrderEditStatus(result.data);
      setItemQuantities({});
      setModifiedItems(new Set());
      setSelectedItemIds([]);
      setOrderDetails(result.data);
      setFilteredItems(result.data.items);
      setOrderType(result.data.orderType);
      setOrderTypeId(result.data.orderTypeId);
      setDeliveryDate(dayjs(result.data.deliveryDate, 'DD-MM-YYYY'));
      setCutOffDate(dayjs(result.data.cutOffDate, 'DD-MM-YYYY'));
      setOrderKind(result.data.emergencyOrder ? OrderKind.Emergency : OrderKind.Forecast);
      await fetchDeliverySchedules(result.data.orderTypeId, result.data.supplierNumber);

      const itemsResponse = await client.getItems({
        item: '',
        orderType: result.data.orderTypeId,
        storeNumber: selectedStore?.storeNumber,
        categoryId: -1,
        supplier: result.data.supplierNumber,
        pageNumber: 0,
        pageSize: 9999,
        sort: 'Name',
        direction: 'asc',
      });

      setItems(itemsResponse.data.items);

      // Determine the effective minimum order value. For DSD order types the supplier-specific
      // MOV takes precedence; otherwise (and as a fallback) the order-type level MOV applies, so
      // any order type can enforce a minimum order value.
      let supplierMov = 0;
      if (result.data.supplierNumber && isOrderTypeDSD(result.data.orderTypeId)) {
        try {
          const supplierResponse = await client.getSuppliers(selectedStore?.storeNumber);
          if (supplierResponse.success && supplierResponse.data?.length > 0) {
            const matchedSupplier = supplierResponse.data.find(
              (s) => s.number === result.data.supplierNumber,
            );
            supplierMov = matchedSupplier?.minimumOrderValue ?? 0;
          }
        } catch {
          // Supplier config not critical, continue without it
        }
      }

      let orderTypeMov = 0;
      try {
        const orderTypesResponse = await client.getOrderTypes(selectedStore?.storeNumber);
        if (orderTypesResponse.success && orderTypesResponse.data?.length > 0) {
          const matchedOrderType = orderTypesResponse.data.find(
            (ot) => ot.id === result.data.orderTypeId,
          );
          orderTypeMov = matchedOrderType?.minimumOrderValue ?? 0;
        }
      } catch {
        // Order type config not critical, continue without it
      }

      setMinimumOrderValue(supplierMov > 0 ? supplierMov : orderTypeMov);
    } catch (error) {
      handleSnackbarOpen(t('fetchingErrorMessage') + ' ' + error, 'error');
    }
  };

  const disableUnavailableDates = (date: Dayjs) => {
    const today = dayjs();
    const dayjsDate = dayjs(date);

    if (isAutoShipOrder(orderTypeId)) {
      return true;
    }

    if (orderKind === OrderKind.Emergency) {
      return dayjsDate.isBefore(today, 'day') || dayjsDate.isSame(today, 'day');
    }
    return !deliverySchedules.some((schedule) =>
      dayjsDate.isSame(dayjs(schedule.deliveryDate, 'DD-MM-YYYY'), 'day'),
    );
  };

  const handleDeliveryDateChange = async (newDate: Dayjs | null) => {
    if (!newDate || orderKind === OrderKind.Emergency) {
      handleSnackbarOpen(t('deliveryChangeWarning'), 'warning');
      return;
    }
    const formattedDate = dayjs(newDate).format('DD-MM-YYYY');
    try {
      const orderResponse = await client.getOrderByDeliveryDate(selectedStore?.storeNumber, orderDetails?.orderTypeId ?? -1, formattedDate, orderDetails?.supplierNumber ?? '');
      if (orderResponse?.data) {
        navigate(`/store/order/${orderResponse.data.id}`);
      }
    } catch (error) {
      handleSnackbarOpen(t('fetchingErrorMessage') + ' ' + error, 'error');
    }
  };

  const loadPodStoreConfiguration = async () => {
    try {
      if (selectedStore?.franchiseName && selectedStore.storeNumber) {
        const response = await podClient.getStoreConfiguration(selectedStore.franchiseName, selectedStore.storeNumber);
        setIsPodEnabled(response.isPodEnabled);
      }
    } catch (error) {
      handleSnackbarOpen(t('errorLoadingData'), 'error');
    }
  };

  const loadPredictedDemands = async () => {
    try {
      const uniqueDates = Array.from(new Set(deliverySchedules.map(x => x.deliveryDate)));
      const deliveryDates = uniqueDates.sort((a, b) => dayjs(a, 'DD-MM-YYYY').valueOf() - dayjs(b, 'DD-MM-YYYY').valueOf());

      if (selectedStore?.franchiseName && selectedStore.storeNumber) {
        const conversions: ItemConversion[] = items.map(item => ({
          itemNumber: getInventorySku(item),
          conversionNumber: typeof item.packSize === 'string' ? parseFloat(item.packSize) || 1 : item.packSize || 1,
          isFrozen: item.isFrozen || false,
          minimumOrderQuantity: item.minQuantity ?? null,
        }));

        const predictions = await podClient.getPredictionsForItems(selectedStore.franchiseName, selectedStore.storeNumber, deliveryDates, items.map(x => getInventorySku(x)), conversions);
        setPredictedDemands(predictions);
      }
    } catch (error) {
      handleSnackbarOpen(t('errorLoadingData'), 'error');
    }
  };

  const handleSearch = () => {
    if (orderDetails) {
      const filtered = orderDetails.items.filter(item => {
        const matchesSearchText = productSearch === '' || item.sku.toLowerCase().includes(productSearch.toLowerCase()) || item.name.toLowerCase().includes(productSearch.toLowerCase());
        const matchesCategory = category === -1 || item.categoryId === category;
        return matchesSearchText && matchesCategory;
      });
      setFilteredItems(filtered);
    }
  };


  const handleDeleteOrderItem = async (orderId: number, orderItemId: number) => {
    if (!orderId || !orderItemId) return;
    setLoading(true);
    try {
      const result = await client.deleteOrderItem(selectedStore?.storeNumber, orderId.toString(), orderItemId.toString());
      if (result.success) {
        handleSnackbarOpen(t('deleteSuccess'), 'success');
        await fetchOrderDetails(orderId);
      } else {
        handleSnackbarOpen(t('deleteError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('deleteError') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (itemId: number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedItemIds((prev) => [...prev, itemId]);
    } else {
      setSelectedItemIds((prev) => prev.filter((id) => id !== itemId));
    }
  };

  const handleDeleteSelectedItems = () => {
    if (!orderDetails || selectedItemIds.length === 0) return;

    const selectedSKUs = orderDetails.items
      .filter(item => selectedItemIds.includes(item.id))
      .map(item => item.sku)
      .join(', ');

    const isParEnabled = orderDetails.isParEnabled === true;

    setDialogState({
      open: true,
      title: t(isParEnabled ? 'doNotOrderModalTitle' : 'deleteModalTitle'),
      message: isParEnabled
        ? t('doNotOrderMultiModalMessage')
        : `${t('deleteMultiModalMessage')}: ${selectedSKUs}?`,
      confirmAction: () => handleConfirmMultipleDeletion(),
    });
  };

  const handleConfirmMultipleDeletion = async () => {
    if (!orderDetails) return;

    const isParEnabled = orderDetails.isParEnabled;

    if (isParEnabled) {
      const selectedItems = orderDetails.items.filter(item =>
        selectedItemIds.includes(item.id),
      );

      const updatedQuantities = { ...itemQuantities };

      selectedItems.forEach(item => {
        // ?? ensure delete does NOT behave like clear
        clearClearedQuantity(item.itemId);

        updatedQuantities[item.itemId] = 0;
      });

      setItemQuantities(updatedQuantities);
      setSelectedItemIds([]);
      setDialogState({ ...dialogState, open: false });

      await handleSaveChanges(updatedQuantities);
      return;
    }

    setLoading(true);

    try {
      const request = {
        orderId: orderDetails.id,
        items: selectedItemIds,
      };

      const result = await client.deleteOrderItems(
        selectedStore?.storeNumber,
        request,
      );

      if (result.success) {
        handleSnackbarOpen(t('multipleDeleteSuccess'), 'success');
        setSelectedItemIds([]);
        await fetchOrderDetails(orderDetails.id);
      } else {
        handleSnackbarOpen(t('multipleDeleteError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('multipleDeleteError') + ' ' + error, 'error');
    } finally {
      setLoading(false);
      setDialogState({ ...dialogState, open: false });
    }
  };


  const handleConfirmDeletion = async (itemId: number, itemNumber: number) => {
    if (!orderDetails) return;


    if (isParEnabled) {
      // ?? ensure delete does NOT behave like clear
      clearClearedQuantity(itemNumber);

      const updatedQuantities = {
        ...itemQuantities,
        [itemNumber]: 0,
      };

      setItemQuantities(updatedQuantities);
      setDialogState({ ...dialogState, open: false });
      await handleSaveChanges(updatedQuantities);
      return;
    }

    if (itemQuantities[itemNumber]) {
      setItemQuantities((prev) => {
        const newQuantities = { ...prev };
        delete newQuantities[itemNumber];
        return newQuantities;
      });
    }

    setDialogState({ ...dialogState, open: false });
    await handleDeleteOrderItem(orderDetails.id, itemId);
  };

  const handleOpenDialogForDeletion = (itemId: number, itemNumber: number) => {
    const isParEnabled = orderDetails?.isParEnabled === true;

    setDialogState({
      open: true,
      title: t(isParEnabled ? 'doNotOrderModalTitle' : 'deleteModalTitle'),
      message: t(isParEnabled ? 'doNotOrderModalMessage' : 'deleteModalMessage'),
      confirmAction: () => handleConfirmDeletion(itemId, itemNumber),
    });
  };

  const handleConfirmAction = async () => {
    if (dialogState.confirmAction) {
      await dialogState.confirmAction();
    }
    setDialogState({ ...dialogState, open: false });
  };

  const handleCancelDialog = () => {
    setDialogState({ ...dialogState, open: false });
  };

  const clearFilters = () => {
    setProductSearch('');
    setCategory(-1);
    if (orderDetails) {
      setFilteredItems(orderDetails.items);
    }
  };

  const handleSort = async (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSubmitOrder = async () => {
    if (!selectedStore || !orderDetails) {
      handleSnackbarOpen(t('submitError'), 'error');
      return;
    }

    await handleSaveChanges();

    setLoading(true);
    const result = await client.submitOrder(selectedStore.storeNumber, orderDetails.id.toString(), orderDetails);
    if (result.success) {
      handleSnackbarOpen(t('submitSuccess'), 'success');
      await fetchOrderDetails(orderDetails.id);
    } else {
      handleSnackbarOpen(t('submitFail'), 'error');
    }
    setLoading(false);
  };

  const handleSubmitScheduledOrderAsEmergency = async () => {
    if (!selectedStore || !orderDetails) {
      handleSnackbarOpen(t('submitError'), 'error');
      return;
    }

    if (minimumOrderValue > 0) {
      const orderTotal = orderDetails.items.reduce((total, item) => {
        const qty = getCurrentQuantity(item);
        return total + (qty * item.price);
      }, 0);

      if (orderTotal < minimumOrderValue) {
        handleSnackbarOpen(t('minimumOrderValueNotMet', { value: minimumOrderValue }), 'error');
        return;
      }
    }

    await handleSaveChanges();

    setLoading(true);
    const result = await client.submitScheduledOrderAsEmergencyOrder(selectedStore.storeNumber, orderDetails.id.toString());
    if (result.success) {
      handleSnackbarOpen(t('submitSuccess'), 'success');
      await fetchOrderDetails(orderDetails.id);
    } else {
      handleSnackbarOpen(t('submitFail'), 'error');
    }
    setLoading(false);
  };

  const handleQuantityChange = (itemId: number, newQuantity: number) => {
    if (newQuantity < 0) return;

    clearClearedQuantity(itemId);

    if (itemId && !modifiedItems.has(itemId)) {
      setModifiedItems(prev => new Set(prev.add(itemId)));
    }

    const item = filteredItems.find(x => x.itemId === itemId);
    const isParEnabled = orderDetails?.isParEnabled === true;

    if (newQuantity === 0 && item && !isParEnabled) {
      handleOpenDialogForDeletion(item.id, item.itemId);
    } else {
      setItemQuantities(prevQuantities => ({
        ...prevQuantities,
        [itemId]: newQuantity,
      }));
    }
  };



  const handleSaveChanges = async (quantitiesOverride?: Record<number, number>) => {
    if (!orderDetails) return;

    const updatedItems = buildUpdatedItems(quantitiesOverride ?? itemQuantities);

    const updatedOrder = {
      id: orderDetails.id,
      items: updatedItems,
      orderlySystem: OrderlySystem.OFS,
    };

    try {
      const result = await client.updateOrder(selectedStore?.storeNumber, updatedOrder);

      if (result.success) {
        handleSnackbarOpen(t('updateSuccess'), 'success');
        await fetchOrderDetails(orderDetails.id);
      } else {
        handleSnackbarOpen(t('updateError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('updateError') + ' ' + error, 'error');
    }
  };
  const handleOrderCopyConfirm = async (orderId: number) => {
    setIsCopyOrderModalOpen(false);
    setLoading(true);
    try {
      const result = await client.copyOrder(selectedStore?.storeNumber, orderDetails?.id.toString(), orderId.toString());
      if (result.success) {
        handleSnackbarOpen(t('copySuccess'), 'success');
        await fetchOrderDetails(orderDetails?.id);
      } else {
        handleSnackbarOpen(t('copyError'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('copyError') + ' ' + error, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!selectedStore || !orderDetails) {
      handleSnackbarOpen(t('deleteError'), 'error');
      return;
    }
    setLoading(true);
    const result = await client.deleteOrder(selectedStore.storeNumber, orderDetails.id.toString());
    if (result.success) {
      navigate('/store/orders-history');
    } else {
      handleSnackbarOpen(t('deleteFail'), 'error');
    }
    setLoading(false);
  };

  const handleExitAndSave = async () => {
    setIsExitAndSaveClicked(true);
    if (!loading) {
      if (orderDetails?.status === OrderStatus.Editable && orderDetails?.isAutoship != true) {
        await handleSaveChanges();
      }
      navigate('/store/orders-history');
    }
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getTotalFormattedPrice = (item: OrderItemDetails, orderStatus: string | undefined, currencyCode: string | undefined) => {
    let quantity: number;
    if (orderStatus === OrderStatus.OrderShipped) {
      quantity = item.shippedQuantity;
    } else if (orderStatus === OrderStatus.OrderConfirmed) {
      quantity = item.confirmedQuantity;
    } else {
      quantity = getCurrentQuantity(item);
    }
    return applyRoundAndFormat(item.price * quantity, currencyCode);
  };

  const handleShowPredictedQuantityChange = (event: ChangeEvent<HTMLInputElement>) => {
    setShowPredictedQuantity(event.target.checked);
  };

  const matchPredictedQuantities = () => {
    setItemQuantities((prev) => {
      const newQuantities = JSON.parse(JSON.stringify(prev)) as Record<number, number>;
      orderDetails?.items.forEach((item) => {
        const predictedItem = predictedDemands.find(p => p.itemNumber === getInventorySku({ sku: item?.sku }));
        const predictedDemandItem = predictedItem?.predictedDemandItems?.find(x => deliveryDate?.isSame(x.date, 'day'));

        if (!predictedDemandItem?.hasPrediction) {
          return;
        }

        newQuantities[item.itemId] = predictedDemandItem.quantity;
      });

      return newQuantities;
    });
  };

  const getAditionalItemsWithPredictions = () => {
    const podItems = predictedDemands.map(p => p.itemNumber);
    const orderItems = orderDetails?.items.map(p => p.sku) ?? [];
    const itemsToAdd = items.filter(x => podItems.includes(x.sku) && !orderItems.includes(x.sku)).map(x => {
      const orderItem: OrderItemDetails = {
        id: 0,
        itemId: x.id,
        sku: x.sku,
        name: x.name,
        primaryUom: x.primaryUom,
        orderableUom: x.orderableUom,
        price: x.price,
        gbpPrice: x.gbpPrice,
        categoryId: x.categoryId,
        category: x.category,
        stockOnHand: x.stockOnHand,
        stockOnOrder: x.stockOnOrder,
        quantity: 0,
        confirmedQuantity: 0,
        shippedQuantity: 0,
        imageId: x.imageId,
        thumbnailUrl: x.thumbnailUrl,
        packSize: x.packSize,
        maxQuantity: x.maxQuantity,
        minQuantity: x.minQuantity,
        gtin: x.gtin,
      };

      return orderItem;
    });

    return itemsToAdd;
  };

  const autocompleteOptions = [{ id: -1, name: 'All', parentName: '' }, ...(Array.isArray(categories) ? categories : [])];

  const orderTotal = useMemo(() => {
    if (!orderDetails) return 0;
    return orderDetails.items.reduce((total, item) => {
      const currentQty = getCurrentQuantity(item);
      const qty = (orderDetails.externallyCreatedOrder && currentQty === 0)
        ? (item.shippedQuantity || item.confirmedQuantity || 0)
        : currentQty;
      return total + (qty * item.price);
    }, 0);
  }, [orderDetails, itemQuantities]);

  const hasSubmitScheduledOrdersPermission = hasPermissionTo(['SubmitScheduledOrders']);
  const canSubmitOrder = !loading && orderKind === OrderKind.Emergency && orderDetails?.status === OrderStatus.Editable && orderDetails.items.length > 0;
  const canSubmitScheduledOrder = !loading && hasSubmitScheduledOrdersPermission && orderDetails?.status === OrderStatus.Editable && orderDetails.items.length > 0;

  const quantityError = (item: OrderItemDetails): string | null => {
    if (clearedQuantityItemIds.has(item.itemId)) return null;

    const quantity = itemQuantities[item.itemId] ?? item.quantity;

    if (item.id === 0) return null;

    if (orderDetails?.isParEnabled === true && quantity === 0) return null;

    if ((item.maxQuantity != null) && quantity > item.maxQuantity)
      return `${t('maxQuantityAllowedIs')}: ${item.maxQuantity}`;

    if ((item.minQuantity != null) && quantity < item.minQuantity)
      return `${t('minQuantityAllowedIs')}: ${item.minQuantity}`;

    return null;
  };

  const isQuantityError = (item: OrderItemDetails): boolean => {
    return quantityError(item) != null;
  };

  const anyErrors = (): boolean => {
    return filteredItems.some(x => isQuantityError(x));
  };

  const fixQuantity = () => {
    const itemQuantities2 = { ...itemQuantities };

    for (const key in itemQuantities2) {
      const value = itemQuantities2[key];
      const maxQuant = orderDetails?.items.find(x => x.itemId.toString() == key)?.maxQuantity;
      const minQuant = orderDetails?.items.find(x => x.itemId.toString() == key)?.minQuantity;

      if (orderDetails?.isParEnabled === true && value === 0)
        continue;

      if (maxQuant && value > maxQuant)
        itemQuantities2[key] = maxQuant;

      if (minQuant && value < minQuant)
        itemQuantities2[key] = minQuant;
    }

    setItemQuantities(itemQuantities2);
  };
  const cutOffTimeLeftFormat = (cutOffTimeLeft: CutOffTimeLeft | undefined) => {
    if (!cutOffTimeLeft ||
      cutOffTimeLeft?.days == undefined ||
      !cutOffTimeLeft?.minutes == undefined ||
      !cutOffTimeLeft?.hours == undefined ||
      (cutOffTimeLeft?.days === 0 && cutOffTimeLeft?.hours === 0 && cutOffTimeLeft?.minutes === 0)) {
      return `${t('cutOffHasPassed')}`;
    }

    const daysString = cutOffTimeLeft?.days > 0 ? `${cutOffTimeLeft.days} ${t('days')}, ` : '';
    const hoursString = cutOffTimeLeft?.hours > 0 ? `${cutOffTimeLeft.hours} ${t('hours')}, ` : '';
    const minutesString = `${cutOffTimeLeft?.minutes} ${t('minutes')}`;

    return `${daysString}${hoursString}${minutesString}`;
  };

  const markQuantityAsCleared = (itemId: number) => {
    setClearedQuantityItemIds((prev) => {
      const next = new Set(prev);
      next.add(itemId);
      return next;
    });
  };

  const clearClearedQuantity = (itemId: number) => {
    setClearedQuantityItemIds((prev) => {
      if (!prev.has(itemId)) return prev;

      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  };

  return (
    <PageArea>
      <Grid item
        xs={12}
        mb={10}
        sx={{ textAlign: 'left' }}>
        <BackButton
          handleClick={handleExitAndSave}
          title={t('exitAndSave')}
          isLink={false}
        />
      </Grid>
      <Grid container>
        <Grid item
          container
          xs={12}>
          <Grid item
            container
            spacing={5}
            alignItems={'end'} >
            <Grid item
              xs={12}
              md={6}>
              <PageTitle>{t('order')} {id}</PageTitle>
              <Grid container
                alignItems={'center'}>
                <Grid item>
                  <Typography mr={2}
                    sx={{ fontSize: '18px' }}>
                    {t('status')}
                  </Typography>
                </Grid>
                <Grid item>
                  <OrderStatusChip orderStatus={orderDetails?.status ?? ''}
                    size='medium' />
                </Grid>
              </Grid>
              {orderKind === OrderKind.Forecast && (
                <Typography mt={5}
                  sx={{ fontSize: '18px' }}>{t('timeToOrderCutoff')} <strong>{cutOffTimeLeftFormat(orderDetails?.cutOffTimeLeft)}</strong></Typography>
              )}
            </Grid>
            <Grid item
              xs={12}
              md={2}>
              <Button
                variant="bad"
                disabled={loading || orderDetails?.status !== OrderStatus.Editable || orderDetails?.isAutoship}
                size="lg"
                fullWidth
                onClick={handleDeleteOrder}
                startIcon={<DeleteIcon />}>
                {t('deleteOrder')}
              </Button>
            </Grid>
            <Grid item
              xs={12}
              md={2}>
              <Button
                variant="primary"
                disabled={loading || orderDetails?.status !== OrderStatus.Editable || anyErrors() || orderDetails?.isAutoship}
                size="lg"
                fullWidth
                startIcon={<SaveIcon />}
                onClick={() => handleSaveChanges()}
              >
                {t('save')}
              </Button>
            </Grid>
            <Grid item
              xs={12}
              md={2}>
              <Button
                variant="primary"
                disabled={loading || orderDetails?.status !== OrderStatus.Editable || orderDetails?.isAutoship}
                onClick={() => navigate(`/store/quick-order/${id}`)}
                size="lg"
                fullWidth
                startIcon={<Plus />}>
                {t('addItems')}
              </Button>
            </Grid>
            <Grid item
              xs={12}
              sx={{ textAlign: 'right', mr: '8px' }}>
              <Typography variant='textLG'
                sx={{
                  visibility: showAutosaveMessage ? 'visible' : 'hidden',
                }}
              >
                {t('changesSaved')}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item
          container
          xs={12}
          mt={5}
          sx={{
            border: '1px solid',
            borderColor: theme.palette.custom.gray[300],
          }}>
          <Grid container
            sx={{
              padding: '20px',
            }}
            spacing={2}>
            <Grid item
              xl={3}
              md={isMobile ? 12 : 7}
              xs={isMobile ? 12 : 6}
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              sx={{ padding: 0 }} >
              <Box>
                <DatePickerLocalized
                  label=''
                  useCalendar={true}
                  valueForDateCalendar={deliveryDate}
                  onChangeDateCalendar={handleDeliveryDateChange}
                  shouldDisableDateCalendar={disableUnavailableDates}
                  disabled={loading || orderKind === OrderKind.Emergency}
                  disableHighlightToday={true}
                />
              </Box>

            </Grid>
            <Grid item
              xl={9}
              md={isMobile ? 12 : 5}
              xs={isMobile ? 12 : 6}
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              sx={{ padding: 0 }}
            >
              <Grid container
                spacing={2}>
                <Grid item
                  xl={9}
                  md={12}
                  xs={12}>
                  <Box>
                    <Grid container
                      spacing={2}>
                      <Grid item
                        xl={isOrderTypeDSD(orderDetails?.orderTypeId ?? -1) && !isMobile ? 4 : 6}
                        md={isOrderTypeDSD(orderDetails?.orderTypeId ?? -1) && !isMobile ? 6 : 12}
                        xs={12}
                        p={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('orderType')}
                          disabled
                          value={t(OrderTypeTranslationMap[orderType as keyof typeof OrderTypeTranslationMap]) || orderType}
                        />
                      </Grid>
                      {isOrderTypeDSD(orderDetails?.orderTypeId ?? -1) && (
                        <Grid item
                          xl={4}
                          md={!isMobile ? 6 : 12}
                          xs={12}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            label={t('supplier')}
                            disabled
                            value={orderDetails?.supplierName}
                          />
                        </Grid>
                      )}
                      {orderKind === OrderKind.Emergency && (
                        <Grid item
                          xl={isOrderTypeDSD(orderDetails?.orderTypeId ?? -1) ? 4 : 6}
                          md={12}
                          sm={12}
                          xs={12}
                        >
                          <Typography component="div">
                            <Grid component="label"
                              container
                              alignItems="center"
                              spacing={1}>
                              <Grid item>{t('emergencyOrder')}</Grid>
                              <Grid item>
                                <Switch
                                  checked={orderKind === OrderKind.Emergency}
                                  name="orderKindSwitch"
                                  readOnly
                                  disabled={loading || orderDetails?.status !== OrderStatus.Editable || !hasPermissionTo(['OrderAdHocOrders']) || orderDetails?.isAutoship}
                                />
                              </Grid>
                            </Grid>
                          </Typography>
                        </Grid>
                      )}
                      <Grid item
                        xl={6}
                        md={6}
                        xs={6}
                      >
                        <DateField
                          label={t('deliveryDate')}
                          disablePast={true}
                          disabled={loading || orderDetails?.status !== OrderStatus.Editable || orderDetails?.isAutoship}
                          slotProps={{ textField: { size: 'small', fullWidth: true } }}
                          readOnly
                          value={deliveryDate}
                        />
                      </Grid>
                      <Grid item
                        xl={6}
                        md={6}
                        xs={6}
                      >
                        <DateField
                          label={t('cutOffDate')}
                          disabled={loading || orderDetails?.status !== OrderStatus.Editable || orderDetails?.isAutoship}
                          slotProps={{ textField: { size: 'small', fullWidth: true } }}
                          readOnly
                          value={cutOffDate}
                        />
                      </Grid>
                      {
                        isPodEnabled && canEditPredictions && orderDetails?.status === OrderStatus.Editable && predictedDemands.length > 0 &&
                        <>
                          <Grid item
                            xs={6}>
                            <FormControlLabel control={
                              <Switch checked={showPredictedQuantity}
                                onChange={handleShowPredictedQuantityChange}
                                disabled={isPodPredictionReadonlyEnabled && !canEditPredictions} />
                            }
                            label={t('showPredictedQuantities')} />
                          </Grid>
                          {
                            showPredictedQuantity &&
                            <Grid item
                              xs={6}>
                              <Button fullWidth
                                variant="primary"
                                disabled={loading}
                                size="lg"
                                startIcon={<MagicWand01 />}
                                onClick={matchPredictedQuantities}>
                                {t('matchPredictedQuantities')}
                              </Button>
                            </Grid>
                          }
                        </>
                      }
                      {minimumOrderValue > 0 && (
                        <Grid item
                          xl={6}
                          md={6}
                          xs={12}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            label={t('minimumOrderValue')}
                            disabled
                            value={applyRoundAndFormat(minimumOrderValue, currencyCode)}
                          />
                        </Grid>
                      )}
                      <Grid item
                        xl={6}
                        md={6}
                        xs={12}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          label={t('orderTotal')}
                          disabled
                          value={applyRoundAndFormat(orderTotal, currencyCode)}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item
                  xl={3}
                  md={12}
                  xs={12}>
                  <Box>
                    <Grid container
                      spacing={2}>
                      <Grid item
                        xs={6}
                        p={3}>
                        <Button
                          variant="primary"
                          disabled={loading}
                          size="lg"
                          fullWidth
                          startIcon={<SearchSm />}
                          onClick={() => setIsOrderAuditModalOpen(true)}
                        >
                          {t('viewAudit')}
                        </Button>
                      </Grid>
                      <Grid item
                        xs={6}>
                        <Button
                          variant="primary"
                          disabled={loading || orderDetails?.status !== OrderStatus.Editable || orderDetails?.isAutoship}
                          size="lg"
                          fullWidth
                          startIcon={<ContentCopyIcon />}
                          onClick={() => setIsCopyOrderModalOpen(true)}
                        >
                          {t('copyOrder')}
                        </Button>
                      </Grid>
                      <Grid item
                        xs={12}>
                        {orderKind === OrderKind.Emergency ? (
                          <Button
                            variant="primary"
                            disabled={!canSubmitOrder}
                            onClick={handleSubmitOrder}
                            size="lg"
                            fullWidth
                          >
                            {t('submitOrder')}
                          </Button>
                        ) : (
                          hasSubmitScheduledOrdersPermission && (
                            <Button
                              variant="primary"
                              disabled={!canSubmitScheduledOrder}
                              onClick={handleSubmitScheduledOrderAsEmergency}
                              size="lg"
                              fullWidth
                            >
                              {t('submitOrder')}
                            </Button>
                          )
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item
            xs={12}>
            <Divider />
          </Grid>
          <Grid item
            container
            sx={{
              px: { xs: 3, md: 6 },
              py: 6,
            }}
            xs={12}
            spacing={5}>
            <Grid item
              xs={12}
              md={3}>
              <TextField
                fullWidth
                size='small'
                placeholder={t('searchByItemNameOrSKU')}
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SvgIcon><SearchSm /></SvgIcon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <Autocomplete
                size='small'
                options={autocompleteOptions}
                getOptionLabel={(option) => isOrderCategory(option.name) ? t(OrderCategoriesTranslationMap[option.name as keyof typeof OrderCategoriesTranslationMap]) : option.name}
                value={categories.find(cat => cat.id === category) ?? autocompleteOptions[0]}
                onChange={(event, newValue) => {
                  setCategory(newValue ? newValue.id : -1);
                }}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('category')}
                    placeholder={t('choose')} />
                )}
              />
            </Grid>
            <Grid item
              xs={12}
              md={2}>
              <Button
                variant="bad"
                disabled={loading || selectedItemIds.length === 0 || orderDetails?.status !== OrderStatus.Editable}
                size="lg"
                fullWidth
                onClick={handleDeleteSelectedItems}
                startIcon={<DeleteIcon />}
              >
                {t('deleteSelected')}
              </Button>
            </Grid>
            <Grid item
              xs={12}
              md={2}>
              <Button fullWidth
                variant="secondary"
                disabled={loading}
                onClick={clearFilters}
                size="lg">
                {t('clearFilters')}
              </Button>
            </Grid>
            <Grid item
              xs={12}
              md={2}>
              <Button fullWidth
                variant="primary"
                disabled={loading}
                onClick={handleSearch}
                size="lg"
                startIcon={<SearchSm />}>
                {t('search')}
              </Button>
            </Grid>
          </Grid>
          <Grid item
            xs={12}>
            <Box sx={{ px: { xs: 3, md: 6 }, pb: 4 }}>
              {orderDetails?.isParEnabled && (
                <Alert
                  severity="info"
                  sx={{ mb: 4 }}>
                  {t('parInfoBanner')}
                </Alert>
              )}
              {loading ? (
                <LoadingWheel />
              ) : (
                <TableContainer
                  component={'div'}
                  sx={{
                    overflowX: 'auto',
                    maxWidth: '100%',
                  }}
                >
                  <Table
                    size="small"
                    aria-label="a table"
                    sx={{
                      minWidth: '700px',
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '60%' }}>
                          {t('itemDetails')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                          <TableSortLabel
                            active={orderBy === 'Ordered'}
                            direction={order}
                            onClick={() => handleSort('Ordered')}
                            sx={{
                              transform: 'translate(15%)',
                            }}
                          >
                            {t('ordered')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '9%' }}>
                          <TableSortLabel
                            active={orderBy === 'Confirmed'}
                            direction={order}
                            onClick={() => handleSort('Confirmed')}
                            sx={{
                              transform: 'translate(15%)',
                            }}
                          >
                            {t('confirmed')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="center"
                          sx={{ verticalAlign: 'middle', fontWeight: 'bold', width: '9%' }}>
                          <TableSortLabel
                            active={orderBy === 'Shipped'}
                            direction={order}
                            onClick={() => handleSort('Shipped')}
                            sx={{
                              transform: 'translate(20%)',
                            }}
                          >
                            {t('shipped')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '9%' }}>
                          <TableSortLabel
                            active={orderBy === 'Cost'}
                            direction={order}
                            onClick={() => handleSort('Cost')}
                            sx={{
                              transform: 'translate(10%)',
                            }}
                          >
                            {t('cost')}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '9%' }}>
                          {t('actions')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredItems.map((item) => {
                        const quantity = getCurrentQuantity(item);
                        const predictedItem = predictedDemands.find(x => x.itemNumber === getInventorySku({ sku: item.sku }));
                        const predictedDemandItem = predictedItem?.predictedDemandItems?.find(x => deliveryDate?.isSame(x.date, 'day'));
                        const showPrediction = showPredictedQuantity && predictedDemandItem?.hasPrediction;
                        const predictedQuantity = predictedDemandItem?.quantity;
                        const borderColor = showPrediction && canEditPredictions && predictedQuantity === quantity ? theme.palette.success[400] : '';
                        const safetyStock = predictedDemandItem?.safetyStock;
                        const predictedDemandQuantity = predictedDemandItem?.predictedDemandQuantity;
                        const displayedParQuantity = item.calculatedParQuantity ?? item.parQuantityApplied;
                        const isParOwnedItem =
                          isParEnabled &&
                          displayedParQuantity != null &&
                          (
                            quantity === displayedParQuantity ||
                            clearedQuantityItemIds.has(item.itemId)
                          );

                        return (
                          <TableRow key={item.itemId}
                            hover
                            onFocus={() => handleItemFocus(item.itemId)}>
                            <TableCell sx={{ width: '60%' }}>
                              <Grid container
                                alignItems={'center'}
                                spacing={1}
                                wrap="nowrap">
                                <Grid item
                                  xs="auto">
                                  <Checkbox
                                    checked={selectedItemIds.includes(item.id)}
                                    onChange={(event) =>
                                      handleCheckboxChange(item.id, event.target.checked)
                                    }
                                    disabled={
                                      loading || orderDetails?.status !== OrderStatus.Editable || orderDetails?.isAutoship
                                    }
                                  />
                                </Grid>
                                <Grid item
                                  sx={{ minWidth: 0, flex: 1 }}>
                                  <ProductDetailsCard item={item}
                                    currencyCode={currencyCode}
                                    showMessageWhenNoCost={showMessageWhenNoCost}
                                    categoryLabel={isOrderCategory(item.category)
                                      ? t(OrderCategoriesTranslationMap[item.category as keyof typeof OrderCategoriesTranslationMap])
                                      : item.category}
                                    parText={isParEnabled && displayedParQuantity != null
                                      ? t('parLevel', { value: displayedParQuantity })
                                      : null} />
                                </Grid>
                              </Grid>
                            </TableCell>
                            <TableCell>
                              <Box className="product-quantity-container"
                                sx={{ display: 'flex', position: 'relative', minWidth: 100 }}>
                                <TextField
                                  label={(showPrediction && canEditPredictions && orderDetails?.status === OrderStatus.Editable) ? t('predictedQuantity', { value: predictedQuantity }) : isParOwnedItem
                                    ? t('parLabel')
                                    : null}
                                  error={isQuantityError(item)}
                                  helperText={quantityError(item)}
                                  size="small"
                                  type="number"
                                  autoComplete='off'
                                  disabled={loading || orderDetails?.status !== OrderStatus.Editable || orderDetails?.isAutoship || (showPrediction && !canEditPredictions)}
                                  onChange={(e) => {
                                    const value = e.target.value;

                                    if (value === '') {
                                      if (orderDetails?.isParEnabled) {
                                        const itemAlreadyAtPar = isPersistedParOwnedItem(item);

                                        if (!itemAlreadyAtPar) {
                                          setItemQuantities(prev => {
                                            const updated = { ...prev };
                                            delete updated[item.itemId];
                                            return updated;
                                          });

                                          markQuantityAsCleared(item.itemId);
                                          markItemAsModified(item.itemId);
                                        } else {

                                          clearClearedQuantity(item.itemId);
                                          clearModifiedItem(item.itemId);
                                        }
                                      } else {
                                        handleQuantityChange(item.itemId, 0);
                                      }

                                      return;
                                    }

                                    const numeric = Number(value);

                                    if (!isNaN(numeric) && numeric >= 0) {
                                      handleQuantityChange(item.itemId, numeric);
                                    }
                                  }}
                                  value={quantity}
                                  sx={{
                                    width: '100%',
                                    input: {
                                      textAlign: 'center',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      '& fieldset': {
                                        borderColor: borderColor,
                                      },
                                    },
                                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                      display: (showPrediction && !canEditPredictions) ? 'none' : undefined,
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: borderColor,
                                    },
                                  }}
                                ></TextField>
                                {
                                  showPrediction && canEditPredictions && orderDetails?.status === OrderStatus.Editable &&
                                  <Tooltip title={
                                    <span>
                                      {
                                        predictedDemandQuantity != null && (
                                          <>
                                            {t('predictedDemandQuantity', { value: predictedDemandQuantity, uom: item.primaryUom })}
                                            <br />
                                          </>
                                        )
                                      }
                                      {safetyStock != null && t('safetyStock', { value: safetyStock, uom: item.primaryUom })}
                                    </span>
                                  }>
                                    <IconButton aria-label={t('applyPredictedQuantity')}
                                      onClick={() => handleQuantityChange(item.itemId, Number(predictedQuantity))}>
                                      <MagicWand01
                                        color={predictedQuantity === quantity ? theme.palette.success[700] : theme.palette.custom.gray[400]} />
                                    </IconButton>
                                  </Tooltip>
                                }
                              </Box>
                            </TableCell>
                            <TableCell align="center"
                              sx={{ verticalAlign: 'middle' }}>{item.confirmedQuantity}</TableCell>
                            <TableCell align="center"
                              sx={{ verticalAlign: 'middle' }}>{item.shippedQuantity}</TableCell>
                            <TableCell align="center"
                              sx={{ verticalAlign: 'middle' }}>
                              {getTotalFormattedPrice(item, orderDetails?.status, currencyCode)}
                            </TableCell>
                            <TableCell align="center"
                              sx={{ verticalAlign: 'middle' }}>
                              <Tooltip title={item.id === 0 ? t('cannotRemoveItem') : ''}>
                                <span>
                                  <IconButton aria-label={t('delete')}
                                    color="error"
                                    disabled={loading || orderDetails?.status !== OrderStatus.Editable || item.id === 0 || orderDetails?.isAutoship}
                                    onClick={() => handleOpenDialogForDeletion(item.id, item.itemId)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      },
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <QuantityWarningDialog
        setShowQuantityWarningDialog={setShowQuantityWarningDialog}
        message={warningMessage}
        showDialog={showQuantityWarningDialog} />
      <ConfirmationDialog
        open={dialogState.open}
        icon={<DeleteIcon />}
        title={dialogState.title}
        message={dialogState.message}
        confirmButtonText={t('confirm')}
        cancelButtonText={t('cancel')}
        onConfirm={handleConfirmAction}
        onCancel={handleCancelDialog}
      />
      <CopyOrderModal
        currentOrderId={orderDetails?.id ?? 0}
        orderTypeId={orderDetails?.orderTypeId ?? 0}
        open={isCopyOrderModalOpen}
        onClose={() => setIsCopyOrderModalOpen(false)}
        onConfirm={handleOrderCopyConfirm}
      />
      <OrderAuditModal
        currentOrderId={orderDetails?.id ?? 0}
        open={isOrderAuditModalOpen}
        onClose={() => setIsOrderAuditModalOpen(false)}
      />
      <Snackbar open={snackbar.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}>
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <LeavePageModal
        open={blocker.state === 'blocked' && modifiedItems.size > 0}
        onConfirm={handleLeavePageModalConfirm}
        onClose={handleLeavePageModalClose}
        onCancel={handleLeavePageModalCancel}
        onConfirmUrl='/store/order'
        leaveWarningContent={t('saveOnLeaveWarningContent')}
        leaveWarningTitle={t('saveOnLeaveWarningTitle')}
      />
    </PageArea >
  );
};

export default OrderDetailsPage;
